import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  Icon,
  RadioGroup,
  Input,
  Field,
  Badge,
  Tooltip,
  Tabs,
  Toaster,
  createToaster,
  Spinner
} from '@chakra-ui/react'

const toaster = createToaster({
  placement: 'top-end',
  pauseOnPageIdle: true,
})
import { FiArrowLeft, FiArrowRight, FiSend, FiTrash2, FiPlus, FiEdit2, FiGlobe, FiFacebook, FiYoutube, FiLinkedin, FiInstagram, FiCheck, FiZap } from 'react-icons/fi'
import { OnboardingLayout } from '../../components/onboarding/OnboardingLayout'
import { useOnboarding } from '../../contexts/OnboardingContext'
import { InfoTooltip } from '../../components/InfoTooltip'
import { SpotlightLandingPagePreview } from '../../components/onboarding/SpotlightLandingPagePreview'
import { OnboardingSideDrawer } from '../../components/onboarding/drawers/OnboardingSideDrawer'
import { ImproveableTextarea } from '../../components/onboarding/ImproveableTextarea'
import { EmailChipInput } from '../../components/onboarding/EmailChipInput'
import { apiConfig } from '../../config/api'
import { auth } from '../../config/firebase'
import type {
  LandingPageDraft,
  ConversionReadiness,
  BookCallDestination,
  FollowUpSequence,
  isValidEmail,
  isValidUrl
} from '../../types/landing-page'
import { isValidEmail as validateEmail, isValidUrl as validateUrl } from '../../types/landing-page'

// Fixed social media platforms with icons
const SOCIAL_PLATFORMS = [
  { id: 'website', label: 'Website', icon: FiGlobe, placeholder: 'https://yourwebsite.com' },
  { id: 'facebook', label: 'Facebook', icon: FiFacebook, placeholder: 'https://facebook.com/yourpage' },
  { id: 'youtube', label: 'YouTube', icon: FiYoutube, placeholder: 'https://youtube.com/@yourchannel' },
  { id: 'linkedin', label: 'LinkedIn', icon: FiLinkedin, placeholder: 'https://linkedin.com/in/yourprofile' },
  { id: 'instagram', label: 'Instagram', icon: FiInstagram, placeholder: 'https://instagram.com/yourhandle' },
]

// Default autoresponder email content
const DEFAULT_AUTORESPONDER = {
  subject: 'Thanks — we got your message',
  body: `Hi there,

Thanks for reaching out! I've received your message and will get back to you shortly.

In the meantime, feel free to explore my website or check out my social links.

Talk soon!`
}

export const LandingPageBuilderPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const {
    basic_info,
    generated_assets,
    conversion_readiness,
    generateAssets,
    loadLandingPageDraft,
    saveAssets,
    saveLandingPageDraft,
    validateConversionReadiness,
    sendConversionReadinessTest,
    loadProgress,
    isLoading,
    error
  } = useOnboarding()
  
  // Get path prefix from current route
  const getPathPrefix = () => {
    if (location.pathname.startsWith('/for-products/onboarding')) return '/for-products/onboarding'
    if (location.pathname.startsWith('/for-services/onboarding')) return '/for-services/onboarding'
    return '/for-services/onboarding' // Default fallback
  }
  const pathPrefix = getPathPrefix()

  const landingPage = generated_assets.landing_page

  const [draftLandingPage, setDraftLandingPage] = useState<LandingPageDraft | null>(landingPage || null)
  const [draftReadiness, setDraftReadiness] = useState<ConversionReadiness>(conversion_readiness)
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const saveSequenceRef = useRef<number>(0)
  const initialLoadRef = useRef(true)
  const hasAttemptedGenerationRef = useRef(false) // Track if we've already tried to generate/load
  const lastSyncedLandingPageRef = useRef<string>('') // Track last synced state
  const [isEditMode, setIsEditMode] = useState(false)

  // Store callbacks in refs to avoid infinite loops
  const generateAssetsRef = useRef(generateAssets)
  const loadLandingPageDraftRef = useRef(loadLandingPageDraft)
  const saveLandingPageDraftRef = useRef(saveLandingPageDraft)
  const validateConversionReadinessRef = useRef(validateConversionReadiness)

  // Update refs when callbacks change
  useEffect(() => {
    generateAssetsRef.current = generateAssets
    loadLandingPageDraftRef.current = loadLandingPageDraft
    saveLandingPageDraftRef.current = saveLandingPageDraft
    validateConversionReadinessRef.current = validateConversionReadiness
  }, [generateAssets, loadLandingPageDraft, saveLandingPageDraft, validateConversionReadiness])

  // Validation state
  const [emailError, setEmailError] = useState<string | null>(null)
  const [urlError, setUrlError] = useState<string | null>(null)
  
  // Subject line AI improvement state
  const [isImprovingSubject, setIsImprovingSubject] = useState(false)
  const [improvedSubject, setImprovedSubject] = useState<string | null>(null)

  type LandingDrawerTab = 'bookCall' | 'instantReply' | 'followUp' | 'links' | 'offer' | 'trust' | 'faq' | 'hero' | 'about'
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [activeDrawerTab, setActiveDrawerTab] = useState<LandingDrawerTab>('bookCall')

  // Section to drawer tab mapping
  const sectionToTabMap: Record<string, LandingDrawerTab> = {
    hero: 'hero',
    offer: 'offer',
    trust: 'trust',
    about: 'about',
    faq: 'faq',
    links: 'links',
    cta: 'bookCall'
  }

  const readinessStatus = conversion_readiness?.status
  const isReady = Boolean(readinessStatus?.is_ready)

  // Follow-up style presets (locked structure, practitioner chooses style)
  const followupPresets = {
    default_v1: {
      name: 'Standard (recommended)',
      description: 'Immediate confirmation, follow-up after 1 day, gentle reminder after 3 days',
      steps: [
        { delay_minutes: 0, channel: 'email' as const, subject: 'Thanks — we got your message', body: "Thanks for reaching out. We've received your message and will respond shortly." },
        { delay_minutes: 1440, channel: 'email' as const, subject: 'Quick follow-up', body: "Just checking in — is there anything you'd like to share before we connect?" },
        { delay_minutes: 4320, channel: 'email' as const, subject: 'One more note', body: "If you still want help, reply here and we'll take the next step." }
      ]
    },
    warm_direct: {
      name: 'Warm & Direct',
      description: 'Friendly, personal tone with direct calls to action',
      steps: [
        { delay_minutes: 0, channel: 'email' as const, subject: 'Hi! Got your message', body: 'Thanks for reaching out! I read your message and I\'d love to help. Let\'s connect soon.' },
        { delay_minutes: 1440, channel: 'email' as const, subject: 'Still interested?', body: 'Just wanted to check in — are you still looking for help? I\'m here when you\'re ready.' },
        { delay_minutes: 4320, channel: 'email' as const, subject: 'Last check', body: 'If you\'re still interested, reply and we\'ll get started right away.' }
      ]
    },
    professional: {
      name: 'Professional',
      description: 'Formal, structured approach with clear next steps',
      steps: [
        { delay_minutes: 0, channel: 'email' as const, subject: 'Thank you for your inquiry', body: 'We have received your message and will respond within 24 hours. Thank you for your interest.' },
        { delay_minutes: 2880, channel: 'email' as const, subject: 'Follow-up on your inquiry', body: 'We wanted to follow up on your recent inquiry. Please let us know if you have any questions or would like to schedule a consultation.' },
        { delay_minutes: 7200, channel: 'email' as const, subject: 'Final follow-up', body: 'This is our final follow-up regarding your inquiry. If you are still interested, please reply to this message.' }
      ]
    }
  }

  // Keep local draft state aligned with backend state (initial load only)
  useEffect(() => {
    // Serialize landingPage to detect changes
    const serializedLandingPage = JSON.stringify(landingPage)

    // Only sync if landingPage has actually changed and we haven't synced this version yet
    if (landingPage && serializedLandingPage !== lastSyncedLandingPageRef.current && initialLoadRef.current) {
      lastSyncedLandingPageRef.current = serializedLandingPage

      // If landing page is missing practitioner photo but basic_info has it, merge it in
      if (basic_info.photo_url && !landingPage.practitioner?.photo_url) {
        setDraftLandingPage({
          ...landingPage,
          practitioner: {
            ...landingPage.practitioner,
            photo_url: basic_info.photo_url,
            name: landingPage.practitioner?.name || basic_info.name,
            specialty: landingPage.practitioner?.specialty || basic_info.specialty
          }
        })
      } else {
        setDraftLandingPage(landingPage)
      }
      // Mark initial load complete after a short delay to let the first render settle
      setTimeout(() => {
        initialLoadRef.current = false
      }, 100)
    }
  }, [landingPage, basic_info.photo_url, basic_info.name, basic_info.specialty])
  
  useEffect(() => {
    if (conversion_readiness) setDraftReadiness(conversion_readiness)
  }, [conversion_readiness])

  // Pre-populate notification emails with user's account email if empty
  useEffect(() => {
    const notificationEmails = draftReadiness?.notification_emails || []
    if (notificationEmails.length === 0) {
      const userEmail = auth.currentUser?.email || localStorage.getItem('user_email')
      if (userEmail) {
        setDraftReadiness((prev: any) => ({
          ...prev,
          notification_emails: [userEmail]
        }))
      }
    }
  }, []) // Run once on mount
  
  // Pre-populate autoresponder fields when enabled but has empty fields
  // Uses a ref to track if we've already populated to avoid loops
  const hasPopulatedAutoresponderRef = useRef(false)
  
  useEffect(() => {
    const autoresponder = draftReadiness?.email_autoresponder
    if (!autoresponder?.enabled) {
      hasPopulatedAutoresponderRef.current = false
      return
    }
    
    // Check if any field is empty and needs population
    const needsFromName = !autoresponder.from_name
    const needsFromEmail = !autoresponder.from_email
    const needsSubject = !autoresponder.subject
    const needsBody = !autoresponder.body
    
    if (!needsFromName && !needsFromEmail && !needsSubject && !needsBody) {
      return // All fields already filled
    }
    
    if (hasPopulatedAutoresponderRef.current) {
      return // Already tried to populate
    }
    
    hasPopulatedAutoresponderRef.current = true
    
    const userEmail = auth.currentUser?.email || localStorage.getItem('user_email') || ''
    const notificationEmails = draftReadiness?.notification_emails || []
    const defaultEmail = notificationEmails[0] || userEmail
    
    const userName = basic_info?.name || 
                     draftLandingPage?.practitioner?.name || 
                     auth.currentUser?.displayName ||
                     (userEmail ? userEmail.split('@')[0].split(/[._-]/).map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(' ') : '')
    
    setDraftReadiness((prev: any) => ({
      ...prev,
      email_autoresponder: {
        ...prev?.email_autoresponder,
        from_name: prev?.email_autoresponder?.from_name || userName,
        from_email: prev?.email_autoresponder?.from_email || defaultEmail,
        subject: prev?.email_autoresponder?.subject || DEFAULT_AUTORESPONDER.subject,
        body: prev?.email_autoresponder?.body || DEFAULT_AUTORESPONDER.body
      }
    }))
  }, [draftReadiness?.email_autoresponder?.enabled, basic_info?.name, draftLandingPage?.practitioner?.name])

  const isLandingPageIncomplete = (lp: LandingPageDraft | null): boolean => {
    if (!lp?.sections) return true

    const { hero, offer, trust, about, faq, cta } = lp.sections

    const offerBullets = offer?.bullets?.filter(x => typeof x === 'string' && x.trim()) || []
    const trustBullets = trust?.bullets?.filter(x => typeof x === 'string' && x.trim()) || []
    const faqItems = faq?.items?.filter(x => x?.q && x?.a) || []

    return (
      !(hero?.headline?.trim()) ||
      !(hero?.subheadline?.trim()) ||
      offerBullets.length < 3 ||
      trustBullets.length < 2 ||
      !(about?.body?.trim()) ||
      faqItems.length < 2 ||
      !(cta?.headline?.trim())
    )
  }

  useEffect(() => {
    if (isLoading) return
    if (hasAttemptedGenerationRef.current) return // Prevent multiple attempts

    // If draft is missing entirely, generate assets.
    if (!landingPage) {
      hasAttemptedGenerationRef.current = true
      generateAssetsRef.current().catch(console.error)
      return
    }

    // If draft exists but is incomplete, ask backend to backfill draft sections.
    if (isLandingPageIncomplete(landingPage)) {
      hasAttemptedGenerationRef.current = true
      loadLandingPageDraftRef.current().catch(() => {
        // Fallback: try generation if draft endpoint fails
        generateAssetsRef.current().catch(console.error)
      })
    }
  }, [landingPage, isLoading])

  // Debounced autosave for Step 3 config (copy + readiness)
  // Skip autosave during initial load to prevent loops
  // Uses sequence tracking to prevent stale validations
  useEffect(() => {
    if (!draftLandingPage) return
    if (initialLoadRef.current) return // Skip during initial load

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    if (validationTimeoutRef.current) clearTimeout(validationTimeoutRef.current)

    saveTimeoutRef.current = setTimeout(async () => {
      const currentSequence = ++saveSequenceRef.current

      try {
        await saveLandingPageDraftRef.current(draftLandingPage, draftReadiness)

        // Only run validation if no newer save has started
        if (currentSequence === saveSequenceRef.current) {
          validationTimeoutRef.current = setTimeout(async () => {
            // Double-check sequence hasn't changed during the delay
            if (currentSequence === saveSequenceRef.current) {
              try {
                await validateConversionReadinessRef.current()
              } catch (e) {
                console.error('Auto-validation failed:', e)
                toaster.create({
                  title: 'Validation warning',
                  description: 'Could not validate page readiness. Please check your settings.',
                  type: 'warning',
                  duration: 5000
                })
              }
            }
          }, 500)
        }
      } catch (e) {
        console.error('Failed to save Step 3 draft:', e)
        toaster.create({
          title: 'Save failed',
          description: e instanceof Error ? e.message : 'Could not save your changes. Please try again.',
          type: 'error',
          duration: 7000
        })
      }
    }, 900)

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
      if (validationTimeoutRef.current) clearTimeout(validationTimeoutRef.current)
    }
  }, [draftLandingPage, draftReadiness])

  const handleBack = () => {
    navigate(`${pathPrefix}/preview`)
  }

  const handleContinue = async () => {
    // Hard gate: must be conversion-ready before pricing
    try {
      const status = await validateConversionReadiness()
      if (!status?.is_ready) {
        toaster.create({
          title: 'Not ready yet',
          description: 'Please complete all required fields before continuing.',
          type: 'warning',
          duration: 5000
        })
        return
      }
    } catch (e) {
      console.error('Readiness validation failed:', e)
      toaster.create({
        title: 'Validation failed',
        description: 'Could not validate page readiness. Please check your settings and try again.',
        type: 'error',
        duration: 7000
      })
      return
    }

    try {
      // Keep legacy endpoint call for backward compatibility with downstream flows.
      await saveAssets(undefined, draftLandingPage?.cta_config?.cta_type)
      // Reload progress to get updated step (backend sets step to 3)
      // Pass current user to ensure we use the correct user's info
      await loadProgress(auth.currentUser)
    } catch (e) {
      console.error('Failed to save landing page settings:', e)
      toaster.create({
        title: 'Save failed',
        description: 'Could not save landing page settings. Please try again.',
        type: 'error',
        duration: 7000
      })
      return
    }
    navigate(`${pathPrefix}/targeting`)
  }

  const setBookingUrl = (bookingUrl: string) => {
    if (!draftLandingPage) return

    // Validate URL if not empty
    if (bookingUrl && !validateUrl(bookingUrl)) {
      setUrlError('Please enter a valid URL (e.g., https://calendly.com/your-link)')
    } else {
      setUrlError(null)
    }

    setDraftLandingPage({
      ...draftLandingPage,
      cta_config: { ...draftLandingPage.cta_config, booking_url: bookingUrl }
    })
  }

  const setContactEmail = (email: string) => {
    if (!draftLandingPage) return

    // Validate email if not empty
    if (email && !validateEmail(email)) {
      setEmailError('Please enter a valid email address')
    } else {
      setEmailError(null)
    }

    setDraftLandingPage({
      ...draftLandingPage,
      cta_config: { ...draftLandingPage.cta_config, contact_email: email }
    })
  }

  const setBookCallDestination = (destination: BookCallDestination) => {
    if (!draftLandingPage) return
    setDraftLandingPage({
      ...draftLandingPage,
      cta_config: {
        ...draftLandingPage.cta_config,
        book_call_destination: destination
      }
    })
  }

  const setNotificationEmails = (emails: string[]) => {
    setDraftReadiness((prev: any) => ({
      ...prev,
      notification_emails: emails
    }))
    
    // Auto-update reply-to email if autoresponder is enabled and doesn't have one set
    if (emails.length > 0 && draftReadiness?.email_autoresponder?.enabled && !draftReadiness?.email_autoresponder?.from_email) {
      setDraftReadiness((prev: any) => ({
        ...prev,
        email_autoresponder: {
          ...(prev?.email_autoresponder || {}),
          from_email: emails[0]
        }
      }))
    }
  }

  // Extract name from email (e.g., "john.doe@email.com" -> "John Doe")
  const getNameFromEmail = (email: string): string => {
    if (!email) return ''
    const localPart = email.split('@')[0]
    // Split by common separators and capitalize
    return localPart
      .split(/[._-]/)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ')
  }

  // Enable autoresponder with smart defaults
  // Uses already-known user data for frictionless experience
  const enableAutoresponderWithDefaults = () => {
    const notificationEmails = draftReadiness?.notification_emails || []
    const userEmail = auth.currentUser?.email || localStorage.getItem('user_email') || ''
    const defaultEmail = notificationEmails[0] || userEmail
    
    // Get name from multiple sources with fallbacks
    // Priority: basic_info.name > landing page practitioner > Firebase displayName > extract from email
    const defaultName = basic_info?.name || 
                        draftLandingPage?.practitioner?.name || 
                        auth.currentUser?.displayName ||
                        getNameFromEmail(userEmail)
    
    setDraftReadiness((prev: any) => ({
      ...prev,
      email_autoresponder: {
        enabled: true,
        from_name: prev?.email_autoresponder?.from_name || defaultName,
        from_email: prev?.email_autoresponder?.from_email || defaultEmail,
        subject: prev?.email_autoresponder?.subject || DEFAULT_AUTORESPONDER.subject,
        body: prev?.email_autoresponder?.body || DEFAULT_AUTORESPONDER.body
      }
    }))
  }

  // Disable autoresponder
  const disableAutoresponder = () => {
    setDraftReadiness((prev: any) => ({
      ...prev,
      email_autoresponder: {
        ...(prev?.email_autoresponder || {}),
        enabled: false
      }
    }))
  }

  // Improve subject line with AI
  const handleImproveSubject = async () => {
    const currentSubject = draftReadiness?.email_autoresponder?.subject || ''
    if (!currentSubject.trim() || currentSubject.trim().length < 5) return

    setIsImprovingSubject(true)
    setImprovedSubject(null)

    try {
      const token = localStorage.getItem('firebase_id_token')
      if (!token) throw new Error('Not authenticated')

      const response = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/onboarding/improve-text`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: currentSubject,
          context: 'autoresponder_subject',
          field_type: 'ad_headline', // Use headline type for short text
          onboarding_mode: basic_info?.specialty ? 'service' : undefined
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to improve text' }))
        throw new Error(errorData.detail || `HTTP ${response.status}`)
      }

      const data = await response.json()
      setImprovedSubject(data.improved_text)
    } catch (err: any) {
      console.error('Failed to improve subject:', err)
      toaster.create({
        title: 'Improvement failed',
        description: err.message || 'Could not improve subject. Please try again.',
        type: 'error',
        duration: 5000
      })
    } finally {
      setIsImprovingSubject(false)
    }
  }

  const acceptImprovedSubject = () => {
    if (improvedSubject) {
      setDraftReadiness((prev: any) => ({
        ...prev,
        email_autoresponder: {
          ...(prev?.email_autoresponder || {}),
          subject: improvedSubject
        }
      }))
      setImprovedSubject(null)
    }
  }

  const setWebsiteUrl = (url: string) => {
    if (!draftLandingPage) return
    setDraftLandingPage({
      ...draftLandingPage,
      links: { ...(draftLandingPage.links || {}), website_url: url }
    })
  }

  const setSocialLinks = (links: Array<{ platform: string; url: string }>) => {
    if (!draftLandingPage) return
    setDraftLandingPage({
      ...draftLandingPage,
      links: { ...(draftLandingPage.links || {}), social_links: links }
    })
  }

  // Helper function to get social link for a specific platform
  const getSocialLink = (platform: string) => {
    if (platform === 'website') return draftLandingPage?.links?.website_url || ''
    const links = draftLandingPage?.links?.social_links || []
    return links.find((l: any) => l.platform === platform)?.url || ''
  }

  // Helper function to set social link for a specific platform
  const setSocialLink = (platform: string, url: string) => {
    if (platform === 'website') {
      setWebsiteUrl(url)
      return
    }
    const currentLinks = [...(draftLandingPage?.links?.social_links || [])]
    const idx = currentLinks.findIndex((l: any) => l.platform === platform)
    if (idx >= 0) {
      if (url) currentLinks[idx].url = url
      else currentLinks.splice(idx, 1) // Remove if empty
    } else if (url) {
      currentLinks.push({ platform, url })
    }
    setSocialLinks(currentLinks)
  }

  // Helper function to update landing page sections
  const updateSection = (section: string, updates: any) => {
    if (!draftLandingPage) return
    setDraftLandingPage((prev: any) => ({
      ...prev,
      sections: {
        ...prev?.sections,
        [section]: { ...prev?.sections?.[section], ...updates }
      }
    }))
  }

  // Helper function to add FAQ item
  const addFaqItem = () => {
    const currentItems = draftLandingPage?.sections?.faq?.items || []
    if (currentItems.length >= 6) return // Max 6 FAQ items
    updateSection('faq', { items: [...currentItems, { q: '', a: '' }] })
  }

  // Helper function to update FAQ item
  const updateFaqItem = (index: number, field: 'q' | 'a', value: string) => {
    const currentItems = [...(draftLandingPage?.sections?.faq?.items || [])]
    currentItems[index] = { ...currentItems[index], [field]: value }
    updateSection('faq', { items: currentItems })
  }

  // Helper function to remove FAQ item
  const removeFaqItem = (index: number) => {
    const currentItems = draftLandingPage?.sections?.faq?.items || []
    updateSection('faq', { items: currentItems.filter((_: any, i: number) => i !== index) })
  }

  // Helper function to add testimonial
  const addTestimonial = () => {
    const currentItems = draftLandingPage?.sections?.trust?.bullets || []
    if (currentItems.length >= 4) return // Max 4 testimonials
    updateSection('trust', { bullets: [...currentItems, ''] })
  }

  // Helper function to update testimonial
  const updateTestimonial = (index: number, value: string) => {
    const currentItems = [...(draftLandingPage?.sections?.trust?.bullets || [])]
    currentItems[index] = value
    updateSection('trust', { bullets: currentItems })
  }

  // Helper function to remove testimonial
  const removeTestimonial = (index: number) => {
    const currentItems = draftLandingPage?.sections?.trust?.bullets || []
    updateSection('trust', { bullets: currentItems.filter((_: any, i: number) => i !== index) })
  }

  const applyFollowupPreset = (templateId: string) => {
    const preset = followupPresets[templateId as keyof typeof followupPresets]
    if (!preset) return
    setDraftReadiness((prev: any) => ({
      ...prev,
      followup_sequence: {
        ...(prev?.followup_sequence || {}),
        enabled: true,
        template_id: templateId,
        steps: preset.steps
      }
    }))
  }

  // Map failure keys to drawer tabs
  const failureKeyToTab: Record<string, LandingDrawerTab> = {
    cta_config_valid: 'bookCall',
    booking_link_valid: 'bookCall',
    notification_emails_valid: 'instantReply',
    autoresponder_configured: 'instantReply',
    followup_enabled: 'followUp',
    landing_page_draft_exists: 'hero'
  }

  // Get actionable failures from readiness status
  const actionableFailures = readinessStatus?.checks
    ? Object.entries(readinessStatus.checks)
        .filter(([_, v]: [string, any]) => !v.ok)
        .map(([key, v]: [string, any]) => ({ 
          key, 
          message: v.message || key.replace(/_/g, ' '),
          tab: failureKeyToTab[key] || 'bookCall'
        }))
    : []

  const openDrawer = (targetTab?: LandingDrawerTab) => {
    if (targetTab) {
      setActiveDrawerTab(targetTab)
    }
    setIsDrawerOpen(true)
  }

  // Tab trigger styling
  const tabTriggerStyles = {
    px: 3,
    py: 2,
    fontSize: 'sm',
    fontWeight: 'medium',
    borderRadius: 'md',
    whiteSpace: 'nowrap' as const,
    _selected: { bg: 'brand.50', color: 'brand.700' },
    _hover: { bg: 'gray.100' }
  }

  return (
    <OnboardingLayout
      currentStep={3}
      totalSteps={5}
      stepLabels={['Basic Setup', 'Preview', 'Landing page', 'Targeting', 'Select package']}
    >
      <VStack gap={8} align="stretch" maxW="7xl" mx="auto">
        <VStack gap={4} textAlign="center">
          <Heading size={{ base: '2xl', md: '3xl' }} color="gray.900">
            Your Landing Page
          </Heading>
          <Text fontSize="lg" color="gray.600" maxW="3xl">
            Spotlight built this for you. Review it, make optional tweaks, and confirm where leads should go.
          </Text>
        </VStack>

        {error && (
          <Box
            p={4}
            bg="red.50"
            borderWidth="1px"
            borderColor="red.200"
            borderRadius="md"
            color="red.700"
          >
            <Text fontSize="sm">{error}</Text>
          </Box>
        )}

        {isLoading || !draftLandingPage ? (
          <Box textAlign="center" py={14}>
            <VStack gap={4}>
              <Spinner size="xl" color="brand.500" />
              <Text fontSize="lg" color="gray.600">
                Preparing your landing page…
              </Text>
            </VStack>
          </Box>
        ) : (
          <VStack gap={4} align="stretch">
            {/* Stage header + actions */}
            <VStack gap={3} align="stretch">
              <HStack justify="space-between" align="center" flexWrap="wrap">
                <HStack gap={2} flexWrap="wrap">
                  <Tooltip.Root closeDelay={300} openDelay={200}>
                    <Tooltip.Trigger asChild>
                      <Button
                        size="sm"
                        variant={isEditMode ? 'solid' : 'outline'}
                        leftIcon={<Icon as={FiEdit2} />}
                        onClick={() => openDrawer('hero')}
                      >
                        {isEditMode ? 'Editing text' : 'Edit page text'}
                      </Button>
                    </Tooltip.Trigger>
                    <Tooltip.Positioner>
                      <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm">
                        <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                        Edit headlines, descriptions, and all text content on your landing page
                      </Tooltip.Content>
                    </Tooltip.Positioner>
                  </Tooltip.Root>
                  <Tooltip.Root closeDelay={300} openDelay={200}>
                    <Tooltip.Trigger asChild>
                      <Button size="sm" variant="outline" onClick={() => openDrawer('bookCall')}>
                        How people reach you
                      </Button>
                    </Tooltip.Trigger>
                    <Tooltip.Positioner>
                      <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm">
                        <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                        Configure your call-to-action: calendar booking, contact form, or email
                      </Tooltip.Content>
                    </Tooltip.Positioner>
                  </Tooltip.Root>
                  <Tooltip.Root closeDelay={300} openDelay={200}>
                    <Tooltip.Trigger asChild>
                      <Button size="sm" variant="outline" onClick={() => openDrawer('links')}>
                        Your links
                      </Button>
                    </Tooltip.Trigger>
                    <Tooltip.Positioner>
                      <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm">
                        <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                        Add your website and social media links for extra credibility
                      </Tooltip.Content>
                    </Tooltip.Positioner>
                  </Tooltip.Root>
                  <Tooltip.Root closeDelay={300} openDelay={200}>
                    <Tooltip.Trigger asChild>
                      <Button size="sm" variant="outline" onClick={() => openDrawer('instantReply')}>
                        Notifications & follow-up
                      </Button>
                    </Tooltip.Trigger>
                    <Tooltip.Positioner>
                      <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm">
                        <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                        Set up automatic email responses and follow-up sequences
                      </Tooltip.Content>
                    </Tooltip.Positioner>
                  </Tooltip.Root>
                </HStack>
              </HStack>

              <Text fontSize="sm" color="gray.600">
                {isEditMode
                  ? 'Click into any section to edit. Changes save automatically.'
                  : 'Review your page. Click "Edit page text" to make changes.'}
              </Text>
            </VStack>

            {/* Main stage: Landing page preview */}
            <Box display="flex" justifyContent="center">
              <Box w="full" maxW={{ base: '100%', lg: '960px' }}>
                <SpotlightLandingPagePreview
                  landingPage={draftLandingPage}
                  isEditable={isEditMode}
                  onChange={(next) => setDraftLandingPage(next as LandingPageDraft)}
                  onOpenPanel={(panel, options) => {
                    // Map panel type to drawer tab
                    if (panel === 'automation') {
                      openDrawer('instantReply')
                      return
                    }
                    // Map section to appropriate drawer tab
                    const section = options?.section
                    const targetTab = section ? sectionToTabMap[section] : undefined
                    openDrawer(targetTab || 'bookCall')
                  }}
                />
              </Box>
            </Box>
          </VStack>
        )}

        {/* Unified drawer */}
        <OnboardingSideDrawer
          open={isDrawerOpen}
          onOpenChange={(e) => setIsDrawerOpen(e.open)}
          header={
            <VStack align="stretch" gap={1}>
              <Heading size="md">
                Edit this page
              </Heading>
              <Text fontSize="sm" color="gray.600">
                {activeDrawerTab === 'hero'
                  ? "Edit your main headline and subheadline - the first thing visitors see."
                  : activeDrawerTab === 'offer'
                    ? 'Edit what you offer to visitors.'
                    : activeDrawerTab === 'trust'
                      ? 'Add testimonials and trust signals.'
                      : activeDrawerTab === 'about'
                        ? 'Tell visitors about yourself and your approach.'
                        : activeDrawerTab === 'faq'
                          ? 'Answer common questions visitors have.'
                          : activeDrawerTab === 'bookCall'
                            ? 'Choose what happens when visitors click your call-to-action.'
                            : activeDrawerTab === 'instantReply'
                              ? 'Set where leads go and how you respond.'
                              : activeDrawerTab === 'followUp'
                                ? 'Automatically follow up with visitors who haven\'t responded.'
                                : activeDrawerTab === 'links'
                                  ? 'Add your website and social links.'
                                  : 'Configure your landing page settings.'}
              </Text>
            </VStack>
          }
          footer={
            <Button colorPalette="brand" onClick={() => setIsDrawerOpen(false)} w="full">
              Close
            </Button>
          }
        >
          <Tabs.Root value={activeDrawerTab} onValueChange={(e) => setActiveDrawerTab(e.value as LandingDrawerTab)}>
            <Tabs.List
              mb={4}
              borderBottomWidth="1px"
              borderColor="gray.200"
              flexWrap="wrap"
              gap={1}
              pb={2}
            >
              <Tooltip.Root closeDelay={300} openDelay={200}>
                <Tooltip.Trigger asChild>
                  <Tabs.Trigger value="hero" {...tabTriggerStyles}>Hero</Tabs.Trigger>
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                  <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm">
                    <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                    Edit your main headline and subheadline
                  </Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Root>
              <Tooltip.Root closeDelay={300} openDelay={200}>
                <Tooltip.Trigger asChild>
                  <Tabs.Trigger value="offer" {...tabTriggerStyles}>Offer</Tabs.Trigger>
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                  <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm">
                    <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                    Describe what you offer visitors
                  </Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Root>
              <Tooltip.Root closeDelay={300} openDelay={200}>
                <Tooltip.Trigger asChild>
                  <Tabs.Trigger value="trust" {...tabTriggerStyles}>Testimonials</Tabs.Trigger>
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                  <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm">
                    <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                    Add client testimonials and trust signals
                  </Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Root>
              <Tooltip.Root closeDelay={300} openDelay={200}>
                <Tooltip.Trigger asChild>
                  <Tabs.Trigger value="about" {...tabTriggerStyles}>About</Tabs.Trigger>
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                  <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm">
                    <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                    Tell visitors about yourself
                  </Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Root>
              <Tooltip.Root closeDelay={300} openDelay={200}>
                <Tooltip.Trigger asChild>
                  <Tabs.Trigger value="faq" {...tabTriggerStyles}>FAQ</Tabs.Trigger>
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                  <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm">
                    <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                    Answer common questions
                  </Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Root>
              <Tooltip.Root closeDelay={300} openDelay={200}>
                <Tooltip.Trigger asChild>
                  <Tabs.Trigger value="bookCall" {...tabTriggerStyles}>Book a call</Tabs.Trigger>
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                  <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm">
                    <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                    Configure how visitors book with you
                  </Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Root>
              <Tooltip.Root closeDelay={300} openDelay={200}>
                <Tooltip.Trigger asChild>
                  <Tabs.Trigger value="instantReply" {...tabTriggerStyles}>Instant reply</Tabs.Trigger>
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                  <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm">
                    <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                    Set up automatic responses
                  </Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Root>
              <Tooltip.Root closeDelay={300} openDelay={200}>
                <Tooltip.Trigger asChild>
                  <Tabs.Trigger value="followUp" {...tabTriggerStyles}>Follow-up</Tabs.Trigger>
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                  <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm">
                    <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                    Configure follow-up sequences
                  </Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Root>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <Tabs.Trigger value="links" {...tabTriggerStyles}>Your links</Tabs.Trigger>
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                  <Tooltip.Content bg="gray.900" color="white" px={3} py={2} borderRadius="md" fontSize="sm">
                    <Tooltip.Arrow><Tooltip.ArrowTip fill="gray.900" /></Tooltip.Arrow>
                    Add your website and social links
                  </Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Root>
            </Tabs.List>

            {/* Hero Tab */}
            <Tabs.Content value="hero">
              <Card.Root borderColor="gray.200">
                <Card.Body p={6}>
                  <VStack gap={5} align="stretch">
                    <HStack gap={2}>
                      <Heading size="sm" color="gray.900">
                        Hero Section
                      </Heading>
                      <InfoTooltip
                        content="This is the first thing visitors see. Make it clear and compelling."
                        ariaLabel="Hero section help"
                      />
                    </HStack>
                    <Text fontSize="xs" color="gray.600">
                      Edit your main headline and subheadline.
                    </Text>

                    <Field.Root>
                      <Field.Label fontSize="sm" fontWeight="semibold" color="gray.800">
                        Headline
                      </Field.Label>
                      <ImproveableTextarea
                        value={draftLandingPage?.sections?.hero?.headline || ''}
                        onChange={(value) => updateSection('hero', { headline: value })}
                        context="hero_headline"
                        fieldType="landing_page_headline"
                        onboardingMode={basic_info?.specialty ? 'service' : undefined}
                        rows={2}
                        placeholder="Your main promise or value proposition"
                        maxLength={120}
                      />
                    </Field.Root>

                    <Field.Root>
                      <Field.Label fontSize="sm" fontWeight="semibold" color="gray.800">
                        Subheadline
                      </Field.Label>
                      <ImproveableTextarea
                        value={draftLandingPage?.sections?.hero?.subheadline || ''}
                        onChange={(value) => updateSection('hero', { subheadline: value })}
                        context="hero_subheadline"
                        fieldType="landing_page_body"
                        onboardingMode={basic_info?.specialty ? 'service' : undefined}
                        rows={3}
                        placeholder="Expand on your headline with more detail"
                        maxLength={250}
                      />
                    </Field.Root>
                  </VStack>
                </Card.Body>
              </Card.Root>
            </Tabs.Content>

            {/* Simplified Book a Call tab */}
            <Tabs.Content value="bookCall">
              <Card.Root borderColor="gray.200">
                <Card.Body p={6}>
                  <VStack gap={5} align="stretch">
                    <HStack gap={2}>
                      <Heading size="sm" color="gray.900">
                        When someone clicks "Book a call"
                      </Heading>
                      <InfoTooltip
                        content="This determines how visitors can reach you. Choose a contact form if you prefer to receive inquiries by email, or a calendar scheduler if you want visitors to book directly on your calendar."
                        ariaLabel="Book a call help"
                      />
                    </HStack>
                    <Text fontSize="sm" color="gray.600">
                      Choose what happens when a visitor clicks your call-to-action button.
                    </Text>

                    <RadioGroup.Root
                      value={draftLandingPage?.cta_config?.book_call_destination || 'email'}
                      onValueChange={(e) => setBookCallDestination(e.value as 'calendar' | 'email')}
                    >
                      <VStack align="stretch" gap={3}>
                        <Box
                          as="label"
                          cursor="pointer"
                          borderWidth="2px"
                          borderColor={draftLandingPage?.cta_config?.book_call_destination !== 'calendar' ? 'brand.500' : 'gray.200'}
                          borderRadius="lg"
                          p={4}
                          bg={draftLandingPage?.cta_config?.book_call_destination !== 'calendar' ? 'brand.50' : 'white'}
                          _hover={{ borderColor: 'brand.300', bg: 'brand.50' }}
                          transition="all 0.2s"
                        >
                          <RadioGroup.Item value="email">
                            <RadioGroup.ItemHiddenInput />
                            <HStack justify="space-between" w="full">
                              <VStack align="start" gap={1}>
                                <HStack gap={2}>
                                  <RadioGroup.ItemControl />
                                  <Text fontWeight="medium" color="gray.900">
                                    Show contact form
                                  </Text>
                                </HStack>
                                <Text fontSize="sm" color="gray.500" pl={6}>
                                  Visitors fill out a form, you receive an email
                                </Text>
                              </VStack>
                              {draftLandingPage?.cta_config?.book_call_destination !== 'calendar' && (
                                <Icon as={FiCheck} color="brand.500" boxSize={5} />
                              )}
                            </HStack>
                          </RadioGroup.Item>
                        </Box>
                        <Box
                          as="label"
                          cursor="pointer"
                          borderWidth="2px"
                          borderColor={draftLandingPage?.cta_config?.book_call_destination === 'calendar' ? 'brand.500' : 'gray.200'}
                          borderRadius="lg"
                          p={4}
                          bg={draftLandingPage?.cta_config?.book_call_destination === 'calendar' ? 'brand.50' : 'white'}
                          _hover={{ borderColor: 'brand.300', bg: 'brand.50' }}
                          transition="all 0.2s"
                        >
                          <RadioGroup.Item value="calendar">
                            <RadioGroup.ItemHiddenInput />
                            <HStack justify="space-between" w="full">
                              <VStack align="start" gap={1}>
                                <HStack gap={2}>
                                  <RadioGroup.ItemControl />
                                  <Text fontWeight="medium" color="gray.900">
                                    Show calendar scheduler
                                  </Text>
                                </HStack>
                                <Text fontSize="sm" color="gray.500" pl={6}>
                                  Visitors book directly via your Calendly link
                                </Text>
                              </VStack>
                              {draftLandingPage?.cta_config?.book_call_destination === 'calendar' && (
                                <Icon as={FiCheck} color="brand.500" boxSize={5} />
                              )}
                            </HStack>
                          </RadioGroup.Item>
                        </Box>
                      </VStack>
                    </RadioGroup.Root>

                    {draftLandingPage?.cta_config?.book_call_destination === 'calendar' && (
                      <Field.Root invalid={!!urlError}>
                        <Field.Label fontSize="sm" fontWeight="semibold" color="gray.800">
                          Calendly link
                        </Field.Label>
                        <Input
                          value={draftLandingPage?.cta_config?.booking_url || ''}
                          onChange={(e) => setBookingUrl(e.target.value)}
                          placeholder="https://calendly.com/your-link"
                        />
                        {urlError ? (
                          <Field.ErrorText fontSize="xs">{urlError}</Field.ErrorText>
                        ) : (
                          <Field.HelperText fontSize="xs" color="gray.600">
                            Paste your Calendly, Acuity, or other scheduling link here.
                          </Field.HelperText>
                        )}
                      </Field.Root>
                    )}

                    {draftLandingPage?.cta_config?.book_call_destination === 'email' && (
                      <Field.Root invalid={!!emailError}>
                        <Field.Label fontSize="sm" fontWeight="semibold" color="gray.800">
                          Contact email
                        </Field.Label>
                        <Input
                          type="email"
                          value={draftLandingPage?.cta_config?.contact_email || ''}
                          onChange={(e) => setContactEmail(e.target.value)}
                          placeholder="your@email.com"
                        />
                        {emailError ? (
                          <Field.ErrorText fontSize="xs">{emailError}</Field.ErrorText>
                        ) : (
                          <Field.HelperText fontSize="xs" color="gray.600">
                            Email address where booking requests will be sent.
                          </Field.HelperText>
                        )}
                      </Field.Root>
                    )}
                  </VStack>
                </Card.Body>
              </Card.Root>
            </Tabs.Content>

            <Tabs.Content value="instantReply">
              <VStack gap={6} align="stretch">
                {/* Section A: Lead Notifications */}
                <Card.Root borderColor="gray.200">
                  <Card.Body p={6}>
                    <VStack gap={4} align="stretch">
                      <HStack gap={2}>
                        <Heading size="sm" color="gray.900">
                          Lead Notifications
                        </Heading>
                        <InfoTooltip
                          content="When someone submits a form on your landing page, you'll receive an email notification at these addresses."
                          ariaLabel="Lead notifications help"
                        />
                      </HStack>
                      <Text fontSize="sm" color="gray.600">
                        Where should we notify you about new leads?
                      </Text>
                      <EmailChipInput
                        value={draftReadiness?.notification_emails || []}
                        onChange={setNotificationEmails}
                        placeholder="Type your email and press Enter..."
                        helperText="Press Enter to add an email"
                      />
                    </VStack>
                  </Card.Body>
                </Card.Root>

                {/* Section B: Instant Reply to Visitors */}
                <Card.Root borderColor="gray.200">
                  <Card.Body p={6}>
                    <VStack gap={5} align="stretch">
                      <HStack gap={2}>
                        <Heading size="sm" color="gray.900">
                          Instant Reply to Visitors
                        </Heading>
                        <InfoTooltip
                          content="When someone submits a form, they immediately receive a confirmation email. This builds trust and sets expectations for when they'll hear back from you."
                          ariaLabel="Instant reply help"
                        />
                      </HStack>
                      <Text fontSize="sm" color="gray.600">
                        Send an automatic reply when someone reaches out.
                      </Text>

                      <HStack gap={2}>
                        <Button
                          size="sm"
                          variant={draftReadiness?.email_autoresponder?.enabled ? 'solid' : 'outline'}
                          colorPalette="brand"
                          onClick={() => {
                            if (draftReadiness?.email_autoresponder?.enabled) {
                              disableAutoresponder()
                            } else {
                              enableAutoresponderWithDefaults()
                            }
                          }}
                        >
                          {draftReadiness?.email_autoresponder?.enabled ? 'Enabled' : 'Enable'}
                        </Button>
                        {draftReadiness?.email_autoresponder?.enabled && (
                          <Button
                            size="sm"
                            variant="outline"
                            leftIcon={<Icon as={FiSend} />}
                            onClick={() => sendConversionReadinessTest('autoresponder').catch(console.error)}
                          >
                            Send me a test email
                          </Button>
                        )}
                      </HStack>

                      {draftReadiness?.email_autoresponder?.enabled && (
                        <VStack align="stretch" gap={4} pt={2}>
                          <Field.Root>
                            <Field.Label fontSize="sm" fontWeight="semibold" color="gray.800">
                              From name
                            </Field.Label>
                            <Input
                              value={draftReadiness?.email_autoresponder?.from_name || ''}
                              onChange={(e) =>
                                setDraftReadiness((prev: any) => ({
                                  ...prev,
                                  email_autoresponder: { ...(prev?.email_autoresponder || {}), from_name: e.target.value }
                                }))
                              }
                              placeholder="Your name"
                            />
                          </Field.Root>

                          <Field.Root>
                            <Field.Label fontSize="sm" fontWeight="semibold" color="gray.800">
                              Reply-to email
                            </Field.Label>
                            <Input
                              type="email"
                              value={draftReadiness?.email_autoresponder?.from_email || ''}
                              onChange={(e) =>
                                setDraftReadiness((prev: any) => ({
                                  ...prev,
                                  email_autoresponder: { ...(prev?.email_autoresponder || {}), from_email: e.target.value }
                                }))
                              }
                              placeholder="your@email.com"
                            />
                            <Field.HelperText fontSize="xs" color="gray.600">
                              When leads reply to the auto-response, replies go here.
                            </Field.HelperText>
                          </Field.Root>

                          <Field.Root>
                            <HStack justify="space-between" align="center" mb={1}>
                              <Field.Label fontSize="sm" fontWeight="semibold" color="gray.800" mb={0}>
                                Subject
                              </Field.Label>
                              <Button
                                size="xs"
                                variant="ghost"
                                colorPalette="purple"
                                onClick={handleImproveSubject}
                                loading={isImprovingSubject}
                                disabled={!draftReadiness?.email_autoresponder?.subject?.trim() || !!improvedSubject}
                                leftIcon={<Icon as={FiZap} />}
                                opacity={draftReadiness?.email_autoresponder?.subject?.trim() && !improvedSubject ? 1 : 0.5}
                              >
                                Improve with AI
                              </Button>
                            </HStack>
                            <Input
                              value={draftReadiness?.email_autoresponder?.subject || ''}
                              onChange={(e) =>
                                setDraftReadiness((prev: any) => ({
                                  ...prev,
                                  email_autoresponder: { ...(prev?.email_autoresponder || {}), subject: e.target.value }
                                }))
                              }
                              placeholder="Thanks — we got your message"
                            />
                            {improvedSubject && (
                              <Box
                                p={3}
                                bg="purple.50"
                                borderRadius="md"
                                borderLeft="4px solid"
                                borderColor="purple.500"
                                mt={2}
                              >
                                <VStack align="stretch" gap={2}>
                                  <HStack gap={2}>
                                    <Icon as={FiZap} color="purple.500" />
                                    <Text fontSize="sm" fontWeight="bold" color="purple.700">
                                      Improved:
                                    </Text>
                                  </HStack>
                                  <Text fontSize="sm" color="gray.800">
                                    {improvedSubject}
                                  </Text>
                                  <HStack gap={2}>
                                    <Button size="xs" colorPalette="purple" onClick={acceptImprovedSubject}>
                                      Accept
                                    </Button>
                                    <Button size="xs" variant="outline" onClick={() => setImprovedSubject(null)}>
                                      Keep Original
                                    </Button>
                                  </HStack>
                                </VStack>
                              </Box>
                            )}
                          </Field.Root>

                          <Field.Root>
                            <Field.Label fontSize="sm" fontWeight="semibold" color="gray.800">
                              Message body
                            </Field.Label>
                            <ImproveableTextarea
                              value={draftReadiness?.email_autoresponder?.body || ''}
                              onChange={(value) =>
                                setDraftReadiness((prev: any) => ({
                                  ...prev,
                                  email_autoresponder: { ...(prev?.email_autoresponder || {}), body: value }
                                }))
                              }
                              context="autoresponder_body"
                              fieldType="landing_page_body"
                              onboardingMode={basic_info?.specialty ? 'service' : undefined}
                              rows={6}
                              placeholder="Hi there,&#10;&#10;Thanks for reaching out! I've received your message and will get back to you shortly.&#10;&#10;Talk soon!"
                            />
                          </Field.Root>
                        </VStack>
                      )}
                    </VStack>
                  </Card.Body>
                </Card.Root>
              </VStack>
            </Tabs.Content>

            <Tabs.Content value="followUp">
              <Card.Root borderColor="gray.200">
                <Card.Body p={6}>
                  <VStack gap={5} align="stretch">
                    <HStack gap={2}>
                      <Heading size="sm" color="gray.900">
                        Follow-up messages (optional)
                      </Heading>
                      <InfoTooltip
                        content="If a visitor submits a form but doesn't respond to your reply, these automated follow-up emails gently remind them. This can significantly increase your response rate."
                        ariaLabel="Follow-up messages help"
                      />
                    </HStack>
                    <Text fontSize="xs" color="gray.600">
                      Automatically follow up with visitors who haven't responded.
                    </Text>

                    {!draftReadiness?.followup_sequence?.enabled ? (
                      <VStack align="stretch" gap={3}>
                        {Object.entries(followupPresets).map(([id, preset]) => (
                          <Box
                            key={id}
                            as="button"
                            onClick={() => applyFollowupPreset(id)}
                            borderWidth="2px"
                            borderColor="gray.200"
                            borderRadius="lg"
                            p={4}
                            bg="white"
                            textAlign="left"
                            cursor="pointer"
                            _hover={{ borderColor: 'brand.300', bg: 'brand.50' }}
                            transition="all 0.2s"
                            w="full"
                          >
                            <VStack align="start" gap={1}>
                              <Text fontSize="sm" fontWeight="semibold" color="gray.900">
                                {preset.name}
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                {preset.description}
                              </Text>
                            </VStack>
                          </Box>
                        ))}
                      </VStack>
                    ) : (
                      <VStack align="stretch" gap={2}>
                        <HStack gap={2}>
                          <Button
                            size="sm"
                            variant="solid"
                            colorPalette="brand"
                            onClick={() =>
                              setDraftReadiness((prev: any) => ({
                                ...prev,
                                followup_sequence: { ...(prev?.followup_sequence || {}), enabled: false }
                              }))
                            }
                          >
                            Follow-up: {followupPresets[draftReadiness?.followup_sequence?.template_id as keyof typeof followupPresets]?.name || 'Custom'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            leftIcon={<Icon as={FiSend} />}
                            onClick={() => sendConversionReadinessTest('lead_notification').catch(console.error)}
                          >
                            Send me a test alert
                          </Button>
                        </HStack>
                        <Box bg="gray.50" borderRadius="md" p={3}>
                          <Text fontSize="xs" color="gray.700" fontWeight="semibold" mb={2}>
                            Sequence preview:
                          </Text>
                          <VStack align="stretch" gap={2}>
                            {(draftReadiness?.followup_sequence?.steps || []).map((s: any, idx: number) => (
                              <Box key={idx} fontSize="xs" color="gray.600">
                                <Text fontWeight="medium">
                                  {idx === 0 ? 'Immediately' : idx === 1 ? 'After 1 day' : `After ${Math.round((s.delay_minutes || 0) / 1440)} days`}: {s.subject || 'No subject'}
                                </Text>
                              </Box>
                            ))}
                          </VStack>
                          <Button
                            size="xs"
                            variant="ghost"
                            mt={2}
                            onClick={() => {
                              const currentId = draftReadiness?.followup_sequence?.template_id || 'default_v1'
                              const nextId = currentId === 'default_v1' ? 'warm_direct' : currentId === 'warm_direct' ? 'professional' : 'default_v1'
                              applyFollowupPreset(nextId)
                            }}
                          >
                            Switch style
                          </Button>
                        </Box>
                      </VStack>
                    )}
                  </VStack>
                </Card.Body>
              </Card.Root>
            </Tabs.Content>

            {/* Fixed Social Links with Platform Icons */}
            <Tabs.Content value="links">
              <Card.Root borderColor="gray.200">
                <Card.Body p={6}>
                  <VStack gap={5} align="stretch">
                    <HStack gap={2}>
                      <Heading size="sm" color="gray.900">
                        Your links
                      </Heading>
                      <InfoTooltip
                        content="Add your website and social media profiles. These appear on your landing page and help visitors learn more about you and connect on their preferred platform."
                        ariaLabel="Your links help"
                      />
                    </HStack>
                    <Text fontSize="xs" color="gray.600">
                      Add your website and social media links (optional).
                    </Text>

                    <VStack align="stretch" gap={4}>
                      {SOCIAL_PLATFORMS.map(platform => (
                        <Field.Root key={platform.id}>
                          <Field.Label fontSize="sm" fontWeight="semibold" color="gray.800">
                            <HStack gap={2}>
                              <Icon as={platform.icon} />
                              <Text>{platform.label}</Text>
                            </HStack>
                          </Field.Label>
                          <Input
                            value={getSocialLink(platform.id)}
                            onChange={(e) => setSocialLink(platform.id, e.target.value)}
                            placeholder={platform.placeholder}
                          />
                        </Field.Root>
                      ))}
                    </VStack>
                  </VStack>
                </Card.Body>
              </Card.Root>
            </Tabs.Content>

            {/* Offer Tab with Form-Based Editing */}
            <Tabs.Content value="offer">
              <Card.Root borderColor="gray.200">
                <Card.Body p={6}>
                  <VStack gap={5} align="stretch">
                    <HStack gap={2}>
                      <Heading size="sm" color="gray.900">
                        What You Offer
                      </Heading>
                      <InfoTooltip
                        content="List the key benefits, services, or outcomes visitors can expect when working with you. Focus on what they'll gain rather than technical details."
                        ariaLabel="What you offer help"
                      />
                    </HStack>
                    <Text fontSize="xs" color="gray.600">
                      Describe what visitors get when they work with you.
                    </Text>

                    <Field.Root>
                      <Field.Label fontSize="sm" fontWeight="semibold" color="gray.800">
                        Section headline
                      </Field.Label>
                      <Input
                        value={draftLandingPage?.sections?.offer?.headline || ''}
                        onChange={(e) => updateSection('offer', { headline: e.target.value })}
                        placeholder="What's included"
                      />
                    </Field.Root>

                    <Field.Root>
                      <Field.Label fontSize="sm" fontWeight="semibold" color="gray.800">
                        Benefits (one per line, max 6)
                      </Field.Label>
                      <ImproveableTextarea
                        value={(draftLandingPage?.sections?.offer?.bullets || []).join('\n')}
                        onChange={(value) => updateSection('offer', { 
                          bullets: value.split('\n').filter(Boolean).slice(0, 6) 
                        })}
                        context="offer_bullets"
                        fieldType="landing_page_body"
                        onboardingMode={basic_info?.specialty ? 'service' : undefined}
                        rows={6}
                        placeholder="Personalized guidance&#10;Easy-to-follow plan&#10;Ongoing support"
                      />
                      <Text fontSize="xs" color="gray.600">
                        List the key benefits or outcomes visitors can expect.
                      </Text>
                    </Field.Root>
                  </VStack>
                </Card.Body>
              </Card.Root>
            </Tabs.Content>

            {/* Testimonials Tab with Form-Based Editing */}
            <Tabs.Content value="trust">
              <Card.Root borderColor="gray.200">
                <Card.Body p={6}>
                  <VStack gap={5} align="stretch">
                    <HStack gap={2}>
                      <Heading size="sm" color="gray.900">
                        Testimonials & Trust
                      </Heading>
                      <InfoTooltip
                        content="Add quotes or endorsements from your past clients. These act as social proof and help new visitors trust you. Short, authentic quotes work best."
                        ariaLabel="Testimonials help"
                      />
                    </HStack>
                    <Text fontSize="xs" color="gray.600">
                      Add quotes, testimonials, or trust signals from your clients that show visitors they can rely on you.
                    </Text>

                    <Field.Root>
                      <Field.Label fontSize="sm" fontWeight="semibold" color="gray.800">
                        Section headline
                      </Field.Label>
                      <Input
                        value={draftLandingPage?.sections?.trust?.headline || ''}
                        onChange={(e) => updateSection('trust', { headline: e.target.value })}
                        placeholder="What people say"
                      />
                    </Field.Root>

                    <VStack align="stretch" gap={4}>
                      {(draftLandingPage?.sections?.trust?.bullets || []).map((testimonial: string, idx: number) => (
                        <Box key={idx} bg="gray.50" borderRadius="md" p={4}>
                          <VStack align="stretch" gap={3}>
                            <HStack justify="space-between" align="start">
                              <Text fontSize="xs" color="gray.500" fontWeight="medium">
                                Testimonial {idx + 1}
                              </Text>
                              <Button
                                size="xs"
                                variant="ghost"
                                colorPalette="red"
                                onClick={() => removeTestimonial(idx)}
                              >
                                <Icon as={FiTrash2} />
                              </Button>
                            </HStack>
                            <ImproveableTextarea
                              value={testimonial || ''}
                              onChange={(value) => updateTestimonial(idx, value)}
                              context={`testimonial_${idx}`}
                              fieldType="landing_page_body"
                              onboardingMode={basic_info?.specialty ? 'service' : undefined}
                              placeholder="e.g., &quot;This changed my life! I finally have a plan that works.&quot; — Sarah M."
                              rows={3}
                            />
                          </VStack>
                        </Box>
                      ))}
                      
                      {(draftLandingPage?.sections?.trust?.bullets || []).length < 4 && (
                        <Button
                          size="sm"
                          variant="outline"
                          leftIcon={<Icon as={FiPlus} />}
                          onClick={addTestimonial}
                        >
                          Add testimonial
                        </Button>
                      )}
                    </VStack>
                  </VStack>
                </Card.Body>
              </Card.Root>
            </Tabs.Content>

            {/* About Tab */}
            <Tabs.Content value="about">
              <Card.Root borderColor="gray.200">
                <Card.Body p={6}>
                  <VStack gap={5} align="stretch">
                    <HStack gap={2}>
                      <Heading size="sm" color="gray.900">
                        About You
                      </Heading>
                      <InfoTooltip
                        content="Explain your approach in plain language. Help visitors understand what it feels like to work with you."
                        ariaLabel="About you help"
                      />
                    </HStack>
                    <Text fontSize="xs" color="gray.600">
                      Tell visitors about yourself and your approach.
                    </Text>

                    <Field.Root>
                      <Field.Label fontSize="sm" fontWeight="semibold" color="gray.800">
                        Section headline
                      </Field.Label>
                      <Input
                        value={draftLandingPage?.sections?.about?.headline || ''}
                        onChange={(e) => updateSection('about', { headline: e.target.value })}
                        placeholder="About me"
                      />
                    </Field.Root>

                    <Field.Root>
                      <Field.Label fontSize="sm" fontWeight="semibold" color="gray.800">
                        Your story
                      </Field.Label>
                      <ImproveableTextarea
                        value={draftLandingPage?.sections?.about?.body || ''}
                        onChange={(value) => updateSection('about', { body: value })}
                        context="about_body"
                        fieldType="landing_page_body"
                        onboardingMode={basic_info?.specialty ? 'service' : undefined}
                        rows={6}
                        placeholder="Tell visitors about your background, approach, and what makes you uniquely qualified to help them..."
                      />
                      <Text fontSize="xs" color="gray.600">
                        Share your story and approach in a way that builds trust and connection.
                      </Text>
                    </Field.Root>
                  </VStack>
                </Card.Body>
              </Card.Root>
            </Tabs.Content>

            {/* FAQ Tab with Form-Based Editing */}
            <Tabs.Content value="faq">
              <Card.Root borderColor="gray.200">
                <Card.Body p={6}>
                  <VStack gap={5} align="stretch">
                    <HStack gap={2}>
                      <Heading size="sm" color="gray.900">
                        Frequently Asked Questions
                      </Heading>
                      <InfoTooltip
                        content="Answer common questions visitors might have before reaching out. This reduces friction and helps visitors feel confident about contacting you."
                        ariaLabel="FAQ help"
                      />
                    </HStack>
                    <Text fontSize="xs" color="gray.600">
                      Answer common questions visitors have before reaching out.
                    </Text>

                    <Field.Root>
                      <Field.Label fontSize="sm" fontWeight="semibold" color="gray.800">
                        Section headline
                      </Field.Label>
                      <Input
                        value={draftLandingPage?.sections?.faq?.headline || ''}
                        onChange={(e) => updateSection('faq', { headline: e.target.value })}
                        placeholder="Common questions"
                      />
                    </Field.Root>

                    <VStack align="stretch" gap={4}>
                      {(draftLandingPage?.sections?.faq?.items || []).map((item: any, idx: number) => (
                        <Box key={idx} bg="gray.50" borderRadius="md" p={4}>
                          <VStack align="stretch" gap={3}>
                            <HStack justify="space-between" align="start">
                              <Text fontSize="xs" color="gray.500" fontWeight="medium">
                                Question {idx + 1}
                              </Text>
                              <Button
                                size="xs"
                                variant="ghost"
                                colorPalette="red"
                                onClick={() => removeFaqItem(idx)}
                              >
                                <Icon as={FiTrash2} />
                              </Button>
                            </HStack>
                            <Input
                              value={item.q || ''}
                              onChange={(e) => updateFaqItem(idx, 'q', e.target.value)}
                              placeholder="What's the question?"
                              size="sm"
                            />
                            <ImproveableTextarea
                              value={item.a || ''}
                              onChange={(value) => updateFaqItem(idx, 'a', value)}
                              context={`faq_answer_${idx}`}
                              fieldType="landing_page_body"
                              onboardingMode={basic_info?.specialty ? 'service' : undefined}
                              placeholder="Your answer..."
                              rows={2}
                              size="sm"
                            />
                          </VStack>
                        </Box>
                      ))}
                      
                      {(draftLandingPage?.sections?.faq?.items || []).length < 6 && (
                        <Button
                          size="sm"
                          variant="outline"
                          leftIcon={<Icon as={FiPlus} />}
                          onClick={addFaqItem}
                        >
                          Add question
                        </Button>
                      )}
                    </VStack>
                  </VStack>
                </Card.Body>
              </Card.Root>
            </Tabs.Content>
          </Tabs.Root>
        </OnboardingSideDrawer>

        <HStack gap={4} justify="space-between" pt={4} w="full" align="flex-start">
          <Button
            variant="ghost"
            onClick={handleBack}
          >
            <Icon as={FiArrowLeft} />
            Back (edit preview)
          </Button>
          <VStack align="flex-end" gap={2} flex={1}>
            {!isReady && actionableFailures.length > 0 && (
              <Box
                bg="orange.50"
                borderWidth="1px"
                borderColor="orange.200"
                borderRadius="md"
                p={3}
                w="full"
                maxW="md"
              >
                <HStack gap={2} mb={2}>
                  <Badge colorPalette="orange" variant="subtle">
                    {actionableFailures.length} item{actionableFailures.length !== 1 ? 's' : ''} to fix
                  </Badge>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.900">
                    Required steps:
                  </Text>
                </HStack>
                <VStack align="stretch" gap={1.5}>
                  {actionableFailures.map((failure, idx) => (
                    <HStack
                      key={idx}
                      gap={2}
                      align="start"
                      as="button"
                      onClick={() => openDrawer(failure.tab)}
                      cursor="pointer"
                      p={1.5}
                      mx={-1.5}
                      borderRadius="md"
                      _hover={{ bg: 'orange.100' }}
                      transition="background 0.15s"
                      textAlign="left"
                    >
                      <Icon as={FiInfo} color="orange.600" boxSize={4} mt={0.5} flexShrink={0} />
                      <Text fontSize="sm" color="gray.700" textDecoration="underline" textUnderlineOffset="2px">
                        {failure.message || failure.key.replace(/_/g, ' ')}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            )}
            <Button
              size="lg"
              colorPalette="brand"
              onClick={handleContinue}
              isDisabled={!draftLandingPage || !isReady}
            >
              Continue to targeting
              <Icon as={FiArrowRight} />
            </Button>
          </VStack>
        </HStack>
      </VStack>
      <Toaster toaster={toaster}>{() => null}</Toaster>
    </OnboardingLayout>
  )
}
